import React, { useEffect, useState } from 'react';

import debounce from 'lodash.debounce';

import {
    useFuelTanks,
    useFuelTanksFilter,
} from '@/app/state/app/registers/index.hooks';
import {
    fetchFuelTanks,
    setFuelTanksFilter,
} from '@/app/state/app/registers/tanks';
import {
    showFuelTankDialog,
    showSendRFIDCardsDialog,
} from '@/app/state/ui/dialog';
import Translator from '../../../helpers/TranslationHelper';
import { DEBOUNCE_MID } from '@/app/constants/DebounceValues';
import { useAppDispatch } from '@/app/hooks';
import Pagination from '../../common/Pagination';
import PhraseFilter from '../../common/PhraseFilter';
import FuelTanksTable from './FuelTankTable';
import TableWrapper from '../_shared/TableWrapper';
import { IFuelTank } from './types';
import FuelTank from '../../../models/registers/FuelTank';
import { usePrivileges } from '@/app/state/auth/index.hooks';
import { useHistory } from 'react-router-dom';
import { RouteTankNewPage } from '@/app/pages/paths';
import GroupFilter from '@/app/components/registers/_shared/GroupFilter';

const FuelTanks = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);

    const filter = useFuelTanksFilter();
    const fuelTanks = useFuelTanks();
    const privileges = usePrivileges();
    const history = useHistory();
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(fetchFuelTanks(filter));
    }, [filter]);

    const handleGroupChange = (option: { id: string }) => {
        const updatedFilter = {
            ...filter,
            group: option && option.id,
            start: 0,
        };
        setCurrentPage(1);
        dispatch(setFuelTanksFilter({ fuelTanksFilter: updatedFilter }));
    };

    const handlePageChange = (selectedPageNumber: number) => {
        const targetPageNumber = selectedPageNumber || currentPage;

        const limit = filter.limit || 1;
        const updatedFilter = {
            ...filter,
            start: (targetPageNumber - 1) * limit,
        };
        setCurrentPage(targetPageNumber);
        dispatch(setFuelTanksFilter({ fuelTanksFilter: updatedFilter }));

        const element = document.querySelector(
            '#registersPageContent .content'
        );
        element?.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    const handleFuelTanksFilter = debounce((name: string) => {
        const updatedFilter = {
            ...filter,
            name,
            start: 0,
        };
        setCurrentPage(1);
        dispatch(setFuelTanksFilter({ fuelTanksFilter: updatedFilter }));
    }, DEBOUNCE_MID);

    const handleSendRFIDCardsClick = (fuelTank: IFuelTank) => {
        dispatch(showSendRFIDCardsDialog({ fuelTank }));
    };

    const renderPagination = () => {
        if (!fuelTanks) {
            return null;
        }
        return (
            <Pagination
                totalResults={fuelTanks.total}
                currentPage={currentPage}
                onPageSelect={handlePageChange}
                resultsPerPage={filter.limit}
            />
        );
    };

    const handleAddFuelTankButtonClick = () => {
        if (privileges.newTankEditionPage) {
            history.push(
                RouteTankNewPage +
                    `?returnUrl=${encodeURIComponent(
                        history.location.pathname + history.location.search
                    )}`
            );
        } else {
            dispatch(
                showFuelTankDialog({
                    fuelTank: new FuelTank() as unknown as IFuelTank,
                })
            );
        }
    };

    return (
        <section className="fuel-tanks">
            <header className="header">
                <div className="header-filter">
                    <PhraseFilter
                        placeholder={Translator.translate('Find by name') + ' '}
                        autoFocus={true}
                        onFilter={handleFuelTanksFilter}
                        filter={filter.name || ''}
                    />
                    <GroupFilter
                        name="employee-group"
                        selectedOptionId={filter.group}
                        placeholder={Translator.translate('Find by group')}
                        onOptionSelect={handleGroupChange}
                    />
                </div>
                {privileges.addFuelTank && (
                    <button
                        className="mtl-button primary"
                        onClick={handleAddFuelTankButtonClick}
                    >
                        {Translator.translate('New fuel tank')}
                    </button>
                )}
            </header>
            <TableWrapper>
                <FuelTanksTable
                    fuelTanks={fuelTanks ? fuelTanks.items : undefined}
                    onSendCards={handleSendRFIDCardsClick}
                />
            </TableWrapper>
            {renderPagination()}
        </section>
    );
};

export default FuelTanks;
